
import React from 'react';
import OnboardingLayout from '../../OnboardingLayout';
import { Article } from '@/components/reader-home/types';
import { mockArticles } from '@/components/reader-home/mockData';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface PersonalizedFeedProps {
  formData: any;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious: () => void;
}

const PersonalizedFeed: React.FC<PersonalizedFeedProps> = ({
  formData,
  currentStep,
  totalSteps,
  onNext,
  onPrevious
}) => {
  // Filter articles based on selected interests and journalists
  const getPersonalizedArticles = (): Article[] => {
    if (formData.interests?.length === 0 && formData.journalists?.length === 0) {
      return mockArticles.slice(0, 5);
    }
    
    return mockArticles
      .filter(article => {
        const topicMatch = formData.interests?.some((topic: string) =>
          article.topics.includes(topic)
        );
        const journalistMatch = formData.journalists?.includes(article.authorId);
        
        return topicMatch || journalistMatch;
      })
      .slice(0, 5);
  };
  
  const personalizedArticles = getPersonalizedArticles();
  
  return (
    <OnboardingLayout
      title="Your Personalized Feed"
      subtitle="Based on your interests, here's what your feed could look like"
      currentStep={currentStep}
      totalSteps={totalSteps}
      onNext={onNext}
      onPrevious={onPrevious}
    >
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <p className="text-muted-foreground mb-4">
            We've curated these articles based on the topics and journalists you selected. Your feed will continue to improve as you interact with content on DirectPress.
          </p>
        </div>
        
        <div className="space-y-4">
          {personalizedArticles.map((article) => (
            <Card key={article.id} className="overflow-hidden">
              <div className="flex flex-col md:flex-row">
                <div className="md:w-1/3 h-48 md:h-auto">
                  <img 
                    src={article.image} 
                    alt={article.title} 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="md:w-2/3 flex flex-col">
                  <CardHeader className="pb-2">
                    <div className="flex flex-wrap gap-2 mb-2">
                      {article.topics.slice(0, 3).map((topic) => (
                        <Badge key={topic} variant="outline" className="bg-muted/50">
                          {topic}
                        </Badge>
                      ))}
                    </div>
                    <CardTitle className="text-xl">{article.title}</CardTitle>
                    <CardDescription className="flex justify-between items-center">
                      <span>By {article.author}</span>
                      <span className="text-xs">{article.date} • {article.readTime}</span>
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="py-2">
                    <p className="text-muted-foreground line-clamp-3">
                      {article.excerpt}
                    </p>
                  </CardContent>
                  <CardFooter className="mt-auto pt-2">
                    <div className="flex items-center text-xs text-muted-foreground">
                      {article.hasAudio && (
                        <span className="mr-4">🎧 Audio available</span>
                      )}
                    </div>
                  </CardFooter>
                </div>
              </div>
            </Card>
          ))}
        </div>
        
        <div className="bg-muted/30 rounded-lg p-4 mt-8">
          <h4 className="text-sm font-medium mb-2">How your feed works:</h4>
          <ul className="text-sm text-muted-foreground space-y-2">
            <li>• Articles are personalized based on your interests and followed journalists</li>
            <li>• Your feed will adapt as you read and interact with content</li>
            <li>• You can always adjust your preferences in settings</li>
            <li>• Discover new content through the Trending and Discover tabs</li>
          </ul>
        </div>
      </div>
    </OnboardingLayout>
  );
};

export default PersonalizedFeed;
